"use client";

import { Link } from "@/i18n/navigation";
import posthog from "posthog-js";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { FadeIn } from "@/components/FadeIn";
import { PlanCards, type PlanPriceInfo } from "@/components/PlanCards";

interface StripePrice {
  id: string;
  productId: string;
  unitAmount: number | null;
  currency: string;
  interval: string | undefined;
  trialPeriodDays: number | null | undefined;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  defaultPriceId: string | undefined;
}

interface PricingProps {
  prices: StripePrice[];
  products: StripeProduct[];
  trialPrice?: {
    id: string;
    unitAmount: number | null;
    currency: string;
  } | null;
}

function toPriceInfo(
  price: StripePrice | undefined
): PlanPriceInfo | undefined {
  if (!price) return undefined;
  return {
    id: price.id,
    unitAmount: price.unitAmount,
    currency: price.currency,
  };
}

export function Pricing({ prices, products, trialPrice }: PricingProps) {
  const t = useTranslations("pricing");

  const hobbyProduct = products.find((p) =>
    p.name.toLowerCase().includes("hobby")
  );
  const hobbyPrices = hobbyProduct
    ? prices.filter((p) => p.productId === hobbyProduct.id)
    : [];
  const monthlyPrice = hobbyPrices.find((p) => p.interval === "month");
  const yearlyPrice = hobbyPrices.find((p) => p.interval === "year");

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-accent/5 blur-[120px] rounded-full -z-10" />

      <div className="max-w-5xl mx-auto">
        <FadeIn>
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-6 tracking-tight text-balance">
              {t("titlePart1")}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-4 text-pretty">
              {t("subtitle")}
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={150} direction="up">
          <PlanCards
            monthly={toPriceInfo(monthlyPrice)}
            yearly={toPriceInfo(yearlyPrice)}
            trialPrice={trialPrice}
            renderTrialCta={({ priceId }) => (
              <Link href="/dashboard/start-trial">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full h-12 text-base font-bold rounded-xl"
                  onClick={() =>
                    posthog.capture("trial_cta_clicked", {
                      plan: "trial_pack",
                      location: "landing_pricing",
                      price_id: priceId,
                    })
                  }
                >
                  {t("trialCta")}
                </Button>
              </Link>
            )}
            renderHobbyCta={({ priceId, billing }) => (
              <Link href="/dashboard/start-trial">
                <Button
                  size="lg"
                  className="w-full h-12 text-base font-bold transition-all duration-300 rounded-xl shadow-lg bg-accent text-accent-foreground hover:bg-accent/90 hover:scale-[1.02] active:scale-[0.98] shadow-accent/20"
                  onClick={() =>
                    posthog.capture("trial_cta_clicked", {
                      plan: "hobby",
                      billing_interval: billing,
                      location: "landing_pricing",
                      price_id: priceId,
                    })
                  }
                >
                  {t("ctaTrial")}
                </Button>
              </Link>
            )}
          />
        </FadeIn>

        <FadeIn delay={400}>
          <p className="mt-8 text-center text-xs text-muted-foreground italic">
            {t("audioFootnote")}
          </p>
        </FadeIn>

        <div className="mt-16 text-center space-y-2">
          <p className="text-muted-foreground text-sm font-medium">
            {t("voicesQuestion")}{" "}
            <Link
              href="/voices"
              className="text-accent hover:underline font-bold"
            >
              {t("voicesLink")}
            </Link>
          </p>

        </div>
      </div>
    </section>
  );
}
